import { useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

interface UseSubmitHandlerOptions {
  onSuccess?: () => void;
  onError?: (error: any) => void;
  successMessage?: string;
  errorMessage?: string;
  debounceMs?: number;
}

// تابع کمکی برای استخراج پیام خطا از پاسخ سرور
const extractErrorMessage = (error: any): string[] => {
  const messages: string[] = [];
  
  console.log('Extracting error messages from:', error);
  
  // اگر خطا شامل validation errors باشد
  if (error.errors && typeof error.errors === 'object') {
    console.log('Found errors object:', error.errors);
    
    // استخراج تمام پیام‌های خطا از فیلدهای مختلف
    Object.entries(error.errors).forEach(([fieldName, fieldErrors]: [string, any]) => {
      console.log(`Processing field ${fieldName}:`, fieldErrors);
      
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach((errorMsg: string) => {
          console.log(`Adding validation error: ${errorMsg}`);
          messages.push(errorMsg);
        });
      } else if (typeof fieldErrors === 'string') {
        console.log(`Adding string validation error: ${fieldErrors}`);
        messages.push(fieldErrors);
      }
    });
  }
  
  // اگر خطاهای validation وجود دارد، فقط آنها را نمایش دهیم
  if (messages.length > 0) {
    console.log('Returning validation errors:', messages);
    return messages;
  }
  
  // اگر خطاهای validation وجود ندارد، پیام عمومی را نمایش دهیم
  if (error.message && typeof error.message === 'string') {
    console.log(`Adding general message: ${error.message}`);
    messages.push(error.message);
  }
  
  // اگر خطا شامل payload باشد (برای Redux Toolkit rejectWithValue)
  if (error.payload && typeof error.payload === 'object') {
    console.log('Found payload from rejectWithValue, extracting from it:', error.payload);
    const payloadMessages = extractErrorMessage(error.payload);
    if (payloadMessages.length > 0) {
      return payloadMessages;
    }
  }
  
  console.log('Final extracted messages:', messages);
  
  // اگر هیچ پیامی پیدا نشد، پیام پیش‌فرض
  if (messages.length === 0) {
    messages.push('خطا در انجام عملیات');
  }
  
  return messages;
};

export const useSubmitHandler = (options: UseSubmitHandlerOptions = {}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSubmitTimeRef = useRef<number>(0);

  const {
    onSuccess,
    onError,
    successMessage = 'عملیات با موفقیت انجام شد',
    errorMessage,
    debounceMs = 1000, // حداقل 1 ثانیه بین submit ها
  } = options;

  const handleSubmit = useCallback(
    async (submitFunction: () => Promise<any>) => {
      // بررسی اینکه آیا در حال submit هستیم
      if (isSubmitting) {
        toast.warning('در حال پردازش درخواست قبلی...');
        return;
      }

      // بررسی debounce
      const now = Date.now();
      if (now - lastSubmitTimeRef.current < debounceMs) {
        toast.warning('لطفاً کمی صبر کنید و دوباره تلاش کنید');
        return;
      }

      // پاک کردن timeout قبلی
      if (submitTimeoutRef.current) {
        clearTimeout(submitTimeoutRef.current);
      }

      try {
        setIsSubmitting(true);
        lastSubmitTimeRef.current = now;

        // اجرای تابع submit
        const result = await submitFunction();

        // نمایش پیام موفقیت
        if (successMessage) {
          toast.success(successMessage);
        }

        // فراخوانی callback موفقیت
        if (onSuccess) {
          onSuccess();
        }

        return result;
      } catch (error: any) {
        console.error('Submit error:', error);

        // استخراج پیام‌های خطا
        let errorMessages: string[];
        
        if (errorMessage) {
          console.log('Using custom error message:', errorMessage);
          errorMessages = [errorMessage];
        } else {
          console.log('Extracting error messages from server response');
          errorMessages = extractErrorMessage(error);
        }
        
        console.log('Final error messages to display:', errorMessages);
        
        // نمایش تمام پیام‌های خطا در یک toast
        if (errorMessages.length > 0) {
          const combinedMessage = errorMessages.join('\n');
          console.log('Showing combined toast:', combinedMessage);
          toast.error(combinedMessage);
        }

        // فراخوانی callback خطا
        if (onError) {
          onError(error);
        }

        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, debounceMs, successMessage, errorMessage, onSuccess, onError]
  );

  return {
    isSubmitting,
    handleSubmit,
  };
};
